import { Activity, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import DateTimeDisplay from '../../components/DateTimeDisplay'
import DetailPageTemplate from '../../templates/DetailPageTemplate'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const formatValue = (value) => {
  if (value === undefined || value === null) return '-'
  if (value === '') return '-'
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-'
  return value
}

const getNestedValue = (obj, path) => {
  if (!obj) return undefined

  const keys = path.split('.')
  let value = obj

  for (const key of keys) {
    if (value === null || value === undefined) return undefined
    value = value[key]
  }

  return value
}

// oxlint-disable-next-line no-unused-vars
const FieldRow = ({ label, value, type, device, fieldKey }) => {
  let displayValue = '-'

  const fieldValue = fieldKey.includes('.')
    ? getNestedValue(device, fieldKey)
    : device[fieldKey]

  displayValue = formatValue(fieldValue)

  if (fieldKey === 'organizationId') {
    if (device.organization) {
      displayValue = (
        <Link to={`/organizations/${device.organization.uuid}`}>
          {device.organization.name}
        </Link>
      )
    } else if (
      device.organizationId &&
      typeof device.organizationId === 'object' &&
      device.organizationId !== null
    ) {
      displayValue = (
        <Link to={`/organizations/${device.organizationId.uuid}`}>
          {device.organizationId.name}
        </Link>
      )
    } else {
      displayValue = '-'
    }
  }

  if (type === 'Date' && fieldValue) {
    try {
      displayValue = (
        <DateTimeDisplay date={new Date(fieldValue)} showTime={true} />
      )
    } catch (e) {
      console.error(`Error formatting date: ${fieldValue}`, e)
      displayValue = fieldValue.toString()
    }
  }

  if (fieldKey === 'analysisChains') {
    if (
      Array.isArray(device.analysisChains) &&
      device.analysisChains.length > 0
    ) {
      displayValue = (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {device.analysisChains.map((chain) => (
            <Link key={chain._id} to={`/analysis-chains/${chain.uuid}`}>
              <Chip
                label={chain.name || chain._id}
                clickable
                size="small"
                variant="outlined"
                color="primary"
              />
            </Link>
          ))}
        </Stack>
      )
    } else {
      displayValue = 'No analysis chains selected'
    }
  }

  if (
    typeof fieldValue === 'object' &&
    fieldValue !== null &&
    !Array.isArray(fieldValue) &&
    type === 'Object'
  ) {
    displayValue = (
      <Stack spacing={0.5}>
        {Object.entries(fieldValue).map(([key, val]) => (
          <Typography key={key} variant="body2" sx={{ fontSize: '0.875rem' }}>
            <strong>{key}:</strong> {formatValue(val)}
          </Typography>
        ))}
      </Stack>
    )
  }

  return (
    <Grid
      container
      display="flex"
      alignItems="flex-start"
      gap={2}
      sx={{ py: 0.75 }}
    >
      <Activity mode={label ? 'visible' : 'hidden'}>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ width: 120, flexShrink: 0 }}
        >
          {label}
        </Typography>
      </Activity>
      <Box sx={{ flex: 1 }}>
        {typeof displayValue === 'string' ? (
          <Typography variant="body2">{displayValue}</Typography>
        ) : (
          displayValue
        )}
      </Box>
    </Grid>
  )
}

const deviceDataFields = [
  {
    sectionKey: 'deviceInformation',
    sectionTitle: 'Device Information',
    fields: [
      { key: 'name', label: 'Name', type: 'String' },
      { key: 'common_name', label: 'Common Name', type: 'String' },
      { key: 'slug', label: 'Slug', type: 'String' },
      { key: 'organizationId', label: 'Organization', type: 'String' },
      { key: 'ip', label: 'IP Address', type: 'String' },
    ],
  },
  {
    sectionKey: 'additionalInformation',
    sectionTitle: 'Additional Information',
    fields: [
      { key: 'uuid', label: 'UUID', type: 'String' },
      { key: 'createdAt', label: 'Created At', type: 'Date' },
      { key: 'updatedAt', label: 'Updated At', type: 'Date' },
      {
        key: 'last_file_timestamp',
        label: 'Last File Timestamp',
        type: 'Date',
      },
    ],
  },
  {
    sectionKey: 'analysisChains',
    sectionTitle: 'Analysis Chains',
    fields: [
      {
        key: 'analysisChains',
        label: '',
        type: 'Array',
      },
    ],
  },

  // {
  //   sectionKey: 'deviceHardware',
  //   sectionTitle: 'Hardware Information',
  //   fields: [
  //     { key: 'deviceDetails.pi', label: 'Pi Details', type: 'Object' },
  //     { key: 'deviceDetails.camera', label: 'Camera', type: 'Object' },
  //     { key: 'deviceDetails.lasers', label: 'Lasers', type: 'Object' },
  //     {
  //       key: 'deviceDetails.capillary',
  //       label: 'Capillary',
  //       type: 'Object',
  //     },
  //     { key: 'deviceDetails.sdCard', label: 'SD Card', type: 'Object' },
  //     { key: 'deviceDetails.pumps', label: 'Pumps', type: 'Array' },
  //     { key: 'deviceDetails.tubes', label: 'Tubes', type: 'Array' },
  //   ],
  // },
  // {
  //   sectionKey: 'deviceSoftware',
  //   sectionTitle: 'Software Information',
  //   fields: [
  //     {
  //       key: 'deviceDetails.firmware',
  //       label: 'Firmware',
  //       type: 'Object',
  //     },
  //     {
  //       key: 'deviceDetails.configurationJson',
  //       label: 'Configuration',
  //       type: 'Mixed',
  //     },
  //     {
  //       key: 'deviceDetails.parametersJson',
  //       label: 'Parameters',
  //       type: 'Mixed',
  //     },
  //   ],
  // },
  // {
  //   sectionKey: 'deviceMaintenance',
  //   sectionTitle: 'Service and Maintenance',
  //   fields: [
  //     { key: 'deviceDetails.service', label: 'Service History', type: 'Array' },
  //     {
  //       key: 'deviceDetails.detectedIssues',
  //       label: 'Issues',
  //       type: 'Array',
  //     },
  //     {
  //       key: 'deviceDetails.deviceErrors',
  //       label: 'Errors',
  //       type: 'Object',
  //     },
  //     {
  //       key: 'deviceDetails.note',
  //       label: 'Note',
  //       type: 'Te',
  //     },
  //   ],
  // },
]

const DevicePage = () => {
  const [device, setDevice] = useState(null)
  const [loading, setLoading] = useState(true)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const { uuid } = useParams()
  const navigate = useNavigate()

  useEffect(() => {
    const fetchDevice = async () => {
      try {
        const { data } = await fetchWithAuth(`devices/${uuid}`)
        setDevice(data)
      } catch (error) {
        console.error('Error fetching device:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchDevice()
  }, [uuid])

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`devices/${uuid}`, { method: 'DELETE' })
      navigate('/devices')
    } catch (error) {
      console.error('Error deleting device:', error)
    }
  }

  const actions = (
    <Stack direction="row" spacing={2}>
      <Button
        component={Link}
        to={`/devices/${uuid}/edit`}
        variant="contained"
        color="primary"
      >
        Edit Device
      </Button>
      <Button
        variant="outlined"
        color="error"
        onClick={() => setOpenDeleteDialog(true)}
      >
        Delete
      </Button>
    </Stack>
  )

  const dialogs = (
    <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
      <DialogTitle>Delete Device?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Are you sure you want to delete this device? This action cannot be
          undone.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
        <Button onClick={handleDelete} color="error">
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )

  const prepareSections = () => {
    if (!device) return []

    return deviceDataFields.map((section) => ({
      title: section.sectionTitle,
      content: (
        <Card variant="outlined" sx={{ mb: 1 }}>
          <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
            <Stack spacing={0.5}>
              {section.fields.map((field) => (
                <FieldRow
                  key={field.key}
                  label={field.label}
                  value={field.value}
                  type={field.type}
                  device={device}
                  fieldKey={field.key}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      ),
    }))
  }

  return (
    <DetailPageTemplate
      title={device?.name || 'Device Details'}
      isLoading={loading}
      actions={actions}
      sections={device ? prepareSections() : []}
      dialogs={dialogs}
      breadcrumbs={device ? ['devices', device.name] : undefined}
    />
  )
}

export default DevicePage
